import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Card,
  CircularProgress,
  Alert,
  Stack,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Snackbar,
  CardMedia,
  Checkbox,
} from '@mui/material';
import { Discount, Close, Check, Percent } from '@mui/icons-material';
import { PropertyCouponService } from '../../../../services/PropertyCouponService';
import { PropertyPromotionService } from '../../../../services/PropertyPromotionService';
import { CreateCouponsForSeveralPropertiesDto } from '../../../../types/PropertyCoupon';
import { PropertyListing } from '../../../../types/Property';
import { availableTags } from './CampaignPage.helpers';
import { CouponPreview } from '../../../../components/couponPreview/CouponPreview';

interface CouponFormProps {
  open: boolean;
  onClose: () => void;
  selectedPropertyIds: number[];
  properties?: PropertyListing[];
  onSuccess: () => void;
}

const CouponForm: React.FC<CouponFormProps> = ({
  open,
  onClose,
  selectedPropertyIds,
  properties = [],
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  const [selectedTag, setSelectedTag] = useState('tag_red');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados para descuentos
  const [includeDiscounts, setIncludeDiscounts] = useState(false);
  const [discountType, setDiscountType] = useState<'same' | 'different'>('same');
  const [sameDiscountPrice, setSameDiscountPrice] = useState<string>('');
  const [individualPrices, setIndividualPrices] = useState<Record<number, string>>({});

  // Estado para controlar qué propiedades tendrán descuento
  const [selectedForDiscount, setSelectedForDiscount] = useState<Record<number, boolean>>({});

  // Estados para snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'success' | 'warning' | 'info'
  });

  const couponService = new PropertyCouponService();
  const promotionService = new PropertyPromotionService();


  // Obtener propiedades seleccionadas con sus datos
  const selectedProperties = properties.filter(p => selectedPropertyIds.includes(p.id_Property));

  // Función para calcular el descuento por propiedad
  const calculateDiscountForProperty = (originalPrice: number, newPrice: number) => {
    if (!originalPrice || !newPrice || originalPrice <= 0 || newPrice <= 0) {
      return null;
    }

    if (newPrice >= originalPrice) {
      return { percentage: 0, savings: 0, hasDiscount: false };
    }

    const percentage = ((originalPrice - newPrice) / originalPrice) * 100;
    const savings = originalPrice - newPrice;

    return {
      percentage: parseFloat(percentage.toFixed(1)),
      savings: savings,
      hasDiscount: true
    };
  };

  const handleIndividualPriceChange = (propertyId: number, value: string) => {
    setIndividualPrices(prev => ({
      ...prev,
      [propertyId]: value
    }));
  };

  const handleDiscountToggle = (propertyId: number, checked: boolean) => {
    setSelectedForDiscount(prev => ({
      ...prev,
      [propertyId]: checked
    }));

    // Si se desmarca, limpiar el precio individual
    if (!checked) {
      setIndividualPrices(prev => {
        const newPrices = { ...prev };
        delete newPrices[propertyId];
        return newPrices;
      });
    }
  };

  const showSnackbar = (message: string, severity: 'error' | 'success' | 'warning' | 'info' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const validateDiscounts = (): boolean => {
    if (!includeDiscounts) return true;

    if (discountType === 'same') {
      if (!sameDiscountPrice.trim()) {
        setError('Debe ingresar un precio de descuento');
        return false;
      }
      const price = parseFloat(sameDiscountPrice);
      if (isNaN(price) || price <= 0) {
        setError('El precio de descuento debe ser un número válido mayor a 0');
        return false;
      }
    } else {
      const selectedForDiscountProperties = selectedProperties.filter(
        property => selectedForDiscount[property.id_Property]
      );

      if (selectedForDiscountProperties.length === 0) {
        setError('Debe seleccionar al menos una propiedad para aplicar descuento');
        return false;
      }

      for (const property of selectedForDiscountProperties) {
        const individualPrice = individualPrices[property.id_Property];
        if (!individualPrice || !individualPrice.trim()) {
          setError(`Debe ingresar un precio de descuento para la propiedad: ${property.title}`);
          return false;
        }
        const price = parseFloat(individualPrice);
        if (isNaN(price) || price <= 0) {
          setError(`El precio de descuento para "${property.title}" debe ser un número válido mayor a 0`);
          return false;
        }
      }
    }

    return true;
  };

  const createPromotions = async () => {
    if (!includeDiscounts) return true;

    try {
      console.log('Individual prices:', individualPrices);
      console.log('Selected for discount:', selectedForDiscount);

      let propertiesToPromote;

      if (discountType === 'same') {
        propertiesToPromote = selectedProperties;
      } else {
        propertiesToPromote = selectedProperties.filter(
          property => selectedForDiscount[property.id_Property]
        );
      }

      const promotionPromises = propertiesToPromote.map(async (property) => {
        try {
          const newPrice = discountType === 'same'
            ? parseFloat(sameDiscountPrice)
            : parseFloat(individualPrices[property.id_Property] || '0');

          const promotionData = {
            propertyId: property.id_Property,
            title: title.trim(),
            newPrice: newPrice.toString(),
            colorText: '#FFFFFF',
            colorTag: '#4CAF50',
          };

          const result = await promotionService.create(promotionData);

          if (!result || !result.success) {
            throw new Error(`Error al crear promoción para la propiedad: ${property.title}`);
          }

          return result;
        } catch (error) {
          console.error(`Error creating promotion for property ${property.id_Property}:`, error);
          showSnackbar(`Error al crear promoción para "${property.title}": ${error instanceof Error ? error.message : 'Error desconocido'}`, 'error');
          throw error;
        }
      });

      const results = await Promise.allSettled(promotionPromises);

      // Verificar resultados y mostrar errores específicos
      const failedPromotions = results.filter(result => result.status === 'rejected');
      const successfulPromotions = results.filter(result => result.status === 'fulfilled');

      if (failedPromotions.length > 0) {
        console.error('Failed promotions:', failedPromotions);
        showSnackbar(`Se crearon ${successfulPromotions.length} promociones exitosamente, pero ${failedPromotions.length} fallaron.`, 'warning');

        // Si todas fallaron, lanzar error
        if (failedPromotions.length === results.length) {
          throw new Error('Error al crear todas las promociones');
        }
      }

      return true;
    } catch (error) {
      console.error('Error creating promotions:', error);
      showSnackbar(`Error general al crear promociones: ${error instanceof Error ? error.message : 'Error desconocido'}`, 'error');
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('El título es obligatorio');
      return;
    }

    if (selectedPropertyIds.length === 0) {
      setError('No hay propiedades seleccionadas');
      return;
    }

    if (!validateDiscounts()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Crear cupones
      const dto: CreateCouponsForSeveralPropertiesDto = {
        propertyIds: selectedPropertyIds,
        title: title.trim(),
        selectedTag: `/assets/images/${selectedTag}.png`,
      };

      const couponSuccess = await couponService.createCouponsForSeveralProperties(dto);

      if (!couponSuccess) {
        throw new Error('Error al crear los cupones');
      }

      // Crear promociones si están habilitadas
      if (includeDiscounts) {
        await createPromotions();
      } else {
        showSnackbar('Cupones creados exitosamente', 'success');
      }

      handleClose();
      onSuccess();
    } catch (error) {
      console.error('Error creating coupons/promotions:', error);
      setError('Error inesperado. Inténtalo de nuevo.');
      showSnackbar('Error inesperado al crear cupones. Inténtalo de nuevo.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setSelectedTag('tag_red');
    setIncludeDiscounts(false);
    setDiscountType('same');
    setSameDiscountPrice('');
    setIndividualPrices({});
    setSelectedForDiscount({});
    setError(null);
    setIsSubmitting(false);
    onClose();
  };

  const getSelectedTagInfo = () => {
    return availableTags.find(tag => tag.value === selectedTag) || availableTags[0];
  };

  const formatPrice = (price: number | undefined, currency: string = 'CRC') => {
    if (!price) return 'Precio no disponible';
    return `${currency === 'CRC' ? '₡' : '$'} ${price.toLocaleString()}`;
  };

  const getPropertyImage = (property: PropertyListing) => {
    // Buscar la primera imagen disponible
    const image = property.photos[0];
    return image;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Discount sx={{ color: '#23afa4ff' }} />
            <Typography variant="h6" component="div">
              Crear Cupones para Propiedades
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Se crearán cupones para {selectedPropertyIds.length} propiedades seleccionadas
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Sección de datos del cupón */}
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <TextField
                  label="Título del Cupón"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="Ej: Descuento Especial 20%"
                  error={!!error && !title.trim()}
                  helperText={!title.trim() && error ? 'El título es obligatorio' : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#23afa4ff',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#23afa4ff',
                    },
                  }}
                />

                <FormControl fullWidth variant="outlined">
                  <InputLabel
                    sx={{
                      '&.Mui-focused': {
                        color: '#23afa4ff',
                      },
                    }}
                  >
                    Tipo de Cupón
                  </InputLabel>
                  <Select
                    value={selectedTag}
                    onChange={(e) => setSelectedTag(e.target.value)}
                    label="Tipo de Cupón"
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#23afa4ff',
                      },
                    }}
                  >
                    {availableTags.map((tag) => (
                      <MenuItem key={tag.value} value={tag.value}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: 1,
                              backgroundColor: tag.color,
                              border: '1px solid #ddd',
                            }}
                          />
                          <Typography variant="body2">{tag.label}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider sx={{ my: 2 }} />

                {/* Nueva sección de descuentos */}
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeDiscounts}
                        onChange={(e) => setIncludeDiscounts(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#23afa4ff',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#23afa4ff',
                          },
                        }}
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Percent sx={{ color: '#23afa4ff', fontSize: 18 }} />
                        <Typography variant="body2" fontWeight="600">
                          Incluir descuentos a las propiedades
                        </Typography>
                      </Stack>
                    }
                  />

                  {includeDiscounts && (
                    <Stack spacing={2} sx={{ mt: 2, p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tipo de descuento</InputLabel>
                        <Select
                          value={discountType}
                          onChange={(e) => setDiscountType(e.target.value as 'same' | 'different')}
                          label="Tipo de descuento"
                        >
                          <MenuItem value="same">Mismo descuento para todas</MenuItem>
                          <MenuItem value="different">Diferente descuento para cada una</MenuItem>
                        </Select>
                      </FormControl>

                      {discountType === 'same' ? (
                        <Stack spacing={2}>
                          <TextField
                            label="Nuevo precio"
                            value={sameDiscountPrice}
                            onChange={(e) => setSameDiscountPrice(e.target.value)}
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="Ej: 150000"
                            InputProps={{
                              startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>{'₡'}</Typography>
                            }}
                          />

                          {/* Mostrar descuento por cada propiedad */}
                          {sameDiscountPrice && selectedProperties.length > 0 && (
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: '#e3f2fd',
                                border: '1px solid #2196f3',
                                maxHeight: 350,
                                overflowY: 'auto'
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight="600" color="#1976d2" gutterBottom>
                                Descuento por propiedad:
                              </Typography>
                              <Stack spacing={1}>
                                {selectedProperties.map((property) => {
                                  const discountData = calculateDiscountForProperty(property.price, parseFloat(sameDiscountPrice));

                                  return (
                                    <Box
                                      key={property.id_Property}
                                      sx={{
                                        p: 1.5,
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0'
                                      }}
                                    >
                                      <Typography variant="caption" fontWeight="600" gutterBottom>
                                        {property.title.length > 30 ? `${property.title.substring(0, 30)}...` : property.title}
                                      </Typography>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {formatPrice(property.price, property.currency)} → {formatPrice(parseFloat(sameDiscountPrice), property.currency)}
                                        </Typography>
                                        {discountData && discountData.hasDiscount ? (
                                          <Chip
                                            label={`-${discountData.percentage}%`}
                                            size="small"
                                            sx={{
                                              backgroundColor: '#4caf50',
                                              color: 'white',
                                              fontWeight: 'bold'
                                            }}
                                          />
                                        ) : (
                                          <Chip
                                            label="Sin descuento"
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                              color: '#f57c00',
                                              borderColor: '#f57c00'
                                            }}
                                          />
                                        )}
                                      </Box>
                                      {discountData && discountData.hasDiscount && (
                                        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                          Ahorro: {formatPrice(discountData.savings, property.currency)}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            maxHeight: 500,
                            overflowY: 'auto',
                            pr: 1,
                          }}
                        >
                          <Stack spacing={2}>
                            {selectedProperties.map((property) => {
                              const newPriceValue = individualPrices[property.id_Property];
                              const discountData = newPriceValue ? calculateDiscountForProperty(property.price, parseFloat(newPriceValue)) : null;

                              return (
                                <Card
                                  key={property.id_Property}
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    mb: 1,
                                    backgroundColor: selectedForDiscount[property.id_Property] ? '#f0f9ff' : '#fff',
                                    border: selectedForDiscount[property.id_Property] ? '2px solid #23afa4ff' : '1px solid #e0e0e0',
                                    minWidth: 0,
                                  }}
                                >
                                  <Stack direction="row" spacing={2} alignItems="flex-start">
                                    {/* Imagen de la propiedad */}
                                    <CardMedia
                                      component="img"
                                      sx={{
                                        width: 80,
                                        height: 60,
                                        borderRadius: 1,
                                        objectFit: 'cover',
                                        flexShrink: 0
                                      }}
                                      image={getPropertyImage(property)}
                                      alt={property.title}
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = '/assets/images/no-image-placeholder.jpg';
                                      }}
                                    />

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      {/* Checkbox para seleccionar */}
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={selectedForDiscount[property.id_Property] || false}
                                            onChange={(e) => handleDiscountToggle(property.id_Property, e.target.checked)}
                                            sx={{
                                              color: '#23afa4ff',
                                              '&.Mui-checked': {
                                                color: '#23afa4ff',
                                              },
                                            }}
                                          />
                                        }
                                        label={
                                          <Box>
                                            <Typography
                                              variant="body2"
                                              fontWeight="600"
                                              sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                              }}
                                            >
                                              {property.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Precio actual: {formatPrice(property.price, property.currency)}
                                            </Typography>
                                          </Box>
                                        }
                                        sx={{ alignItems: 'flex-start', m: 0 }}
                                      />

                                      {/* Campo de precio solo si está seleccionado */}
                                      {selectedForDiscount[property.id_Property] && (
                                        <Stack spacing={1} sx={{ mt: 1 }}>
                                          <TextField
                                            label="Nuevo precio"
                                            value={individualPrices[property.id_Property] || ''}
                                            onChange={(e) => handleIndividualPriceChange(property.id_Property, e.target.value)}
                                            fullWidth
                                            size="small"
                                            type="number"
                                            placeholder="Ingrese el nuevo precio"
                                            InputProps={{
                                              startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>{property.currency === 'CRC' ? '₡' : '$'}</Typography>
                                            }}
                                          />

                                          {/* Mostrar descuento calculado en tiempo real */}
                                          {discountData && (
                                            <Box
                                              sx={{
                                                p: 1.5,
                                                borderRadius: 1,
                                                backgroundColor: discountData.hasDiscount ? '#e8f5e8' : '#fff3cd',
                                                border: `1px solid ${discountData.hasDiscount ? '#4caf50' : '#ffc107'}`
                                              }}
                                            >
                                              {discountData.hasDiscount ? (
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                  <Box>
                                                    <Typography variant="caption" fontWeight="600" color="#2e7d32">
                                                      Descuento: {discountData.percentage}%
                                                    </Typography>
                                                    <Typography variant="caption" color="#2e7d32" sx={{ display: 'block' }}>
                                                      Ahorro: {formatPrice(discountData.savings, property.currency)}
                                                    </Typography>
                                                  </Box>
                                                  <Chip
                                                    label={`-${discountData.percentage}%`}
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: '#4caf50',
                                                      color: 'white',
                                                      fontWeight: 'bold'
                                                    }}
                                                  />
                                                </Stack>
                                              ) : (
                                                <Typography variant="caption" color="#f57c00">
                                                  ⚠️ El nuevo precio debe ser menor al precio actual para generar descuento
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                        </Stack>
                                      )}
                                    </Box>
                                  </Stack>
                                </Card>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Grid>

            {/* Preview del cupón */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight="600" color="#333">
                  Vista Previa del Cupón
                </Typography>

                <CouponPreview
                  title={title}
                  selectedTag={selectedTag}
                  getSelectedTagInfo={getSelectedTagInfo}
                />

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#f8fafc',
                    borderRadius: 1,
                    border: '1px solid #e0e4e7',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    <strong>Color:</strong> {getSelectedTagInfo().label}
                  </Typography>
                  {includeDiscounts && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        <strong>Descuentos:</strong> {discountType === 'same' ? 'Mismo precio para todas' : 'Precio individual'}
                      </Typography>
                      {discountType === 'different' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          <strong>Propiedades con descuento:</strong> {Object.values(selectedForDiscount).filter(Boolean).length} de {selectedProperties.length}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Stack>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isSubmitting}
            startIcon={<Close />}
            sx={{
              color: '#666',
              borderColor: '#ddd',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || !title.trim()}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <Check />}
            sx={{
              backgroundColor: '#23afa4ff',
              '&:hover': {
                backgroundColor: '#1e8e8b',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
              },
            }}
          >
            {isSubmitting ? 'Creando...' : `Crear Cupones (${selectedPropertyIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mostrar errores y mensajes */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CouponForm;